/**
 * Account-blocking error views (APP-001 §7, §12).
 *
 * Renders the appropriate blocking view for each non-ready account state. Each
 * surfaces only safe information and the backend request ID (never secrets or
 * internal details).
 */
import { Lock, ShieldAlert } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RequestId } from './RequestId';
import { useSignInHelpers } from '@/auth/useSignInHelpers';
import { getAppLinks } from '@/config/links';
import type { ApiError } from '@/api/errors';

type Kind = 'auth_failed' | 'disabled' | 'unavailable' | 'unknown';

const COPY: Record<Kind, { title: string; description: string }> = {
  auth_failed: {
    title: 'Your session has expired',
    description: 'Please sign in again to continue.',
  },
  disabled: {
    title: 'Account access disabled',
    description:
      'Access to this account has been disabled. Contact your account administrator or support if you believe this is an error.',
  },
  unavailable: {
    title: 'Dashboard temporarily unavailable',
    description: 'The service could not be reached. Please try again shortly.',
  },
  unknown: {
    title: 'We could not complete your request',
    description: 'An unexpected error occurred while resolving your account.',
  },
};

export function AccountErrorView({
  kind,
  error,
  onRetry,
}: {
  kind: Kind;
  error?: ApiError | null;
  onRetry?: () => void;
}) {
  const copy = COPY[kind];
  const { navigateToSignIn } = useSignInHelpers();
  const supportUrl = getAppLinks().support;

  const Icon = kind === 'disabled' ? Lock : ShieldAlert;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6" data-testid={`account-${kind}`}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <Icon className="h-5 w-5" />
          </div>
          <CardTitle>{copy.title}</CardTitle>
          <CardDescription>{copy.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error?.requestId ? <RequestId requestId={error.requestId} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            {kind === 'auth_failed' ? (
              <Button size="sm" onClick={navigateToSignIn}>
                Sign in
              </Button>
            ) : null}
            {onRetry && (kind === 'unavailable' || kind === 'unknown') ? (
              <Button size="sm" variant="outline" onClick={onRetry}>
                Try again
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" asChild>
              <a href={supportUrl} rel="noopener noreferrer">
                Contact support
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
